import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getCollection, listCollections, listScenes } from "../utils/api";
import { Collection, CollectionWithStats, Scene } from "../common/types";
import { useDurationFilter } from "./DurationFilterContext";

export type SceneContextType = {
  scenes?: Scene[];
  refreshScenes: () => void;
  collectionId?: string;
  setCollectionId: (id: string | undefined) => void;
  collections: Collection[];
  refreshCollections: () => void;
  collectionData?: CollectionWithStats;
  refreshCollectionData: () => void;
};

const ScenesContext = createContext<SceneContextType>({
  scenes: [],
  refreshScenes: () => {},
  collectionId: undefined,
  setCollectionId: () => {},
  collections: [],
  refreshCollections: () => {},
  collectionData: undefined,
  refreshCollectionData: () => {}
});

export function ScenesProvider({ children }: { children: React.ReactNode }) {
  const [scenes, setScenes] = useState<SceneContextType["scenes"]>([]);
  const [collections, setCollections] = useState<SceneContextType["collections"]>([]);
  const [collectionId, setCollectionId] = useState<string | undefined>(undefined);
  const [collectionData, setCollectionData] = useState<SceneContextType["collectionData"]>(undefined);
  const { getFromDate, getToDate } = useDurationFilter();


  const refreshScenes = useCallback(async () => {
    try {
      setScenes([]);
      const response = await listScenes(undefined, collectionId);
      setScenes(response);
    } catch (error) {
      console.error("Error fetching scenes:", error);
    }
  }, [collectionId]);

  const refreshCollections = useCallback(async () => {
    try {
      setCollections([]);
      const response = await listCollections();
      setCollections(response);
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  }, []);

  const refreshCollectionData = useCallback(async () => {
    if (!collectionId) return;
    try {
      const result = await getCollection(collectionId, getFromDate(), getToDate());
      setCollectionData(result);
    } catch (error) {
      console.error("Error loading collection:", error);
    }
  }, [collectionId, getFromDate, getToDate]);

  useEffect(() => {
    refreshScenes();
    refreshCollectionData();
    refreshCollections();
  }, [collectionId, refreshScenes, refreshCollectionData, refreshCollections]);

  return (
    <ScenesContext.Provider value={{
      scenes,
      refreshScenes,
      collectionId: collectionId,
      setCollectionId,
      collections,
      refreshCollections,
      collectionData,
      refreshCollectionData,
    }}>
      {children}
    </ScenesContext.Provider>
  );
}

export function useScenes() {
  return useContext(ScenesContext);
}